import type { UsageAdminReport, UsageAdminUser, UsageEventsReport, UsageLimitPolicy, UsageLogEvent, UsageMissesReport, UsageMissSummary, UsageQueryRollup, UsageWindow } from "./types";

type UsageScope = "user" | "key" | "library" | "miss";
export type UsageMode = "activeUsers" | "allUsers" | "sdk";

export type UsageDetailKey = {
  scopeType: UsageScope;
  scopeId: string;
} | null;

export type UsagePagination = {
  userPage: number;
  libraryPage: number;
  pageSize: number;
};

type UsageSummary = {
  scopeType: UsageScope;
  scopeId: string;
  label: string;
  hint: string;
  requests: number;
  successes: number;
  failures: number;
  notFound: number;
  limitHits: number;
  updatedAt: string;
  signedUpAt?: string;
  lastLoginAt?: string;
  emailVerifiedAt?: string;
  status?: string;
};

const windowOrder = ["minute", "hour", "five_hour", "month"];

export function renderUsageReport(
  report: UsageAdminReport,
  detail: UsageDetailKey,
  search = "",
  pages: UsagePagination,
  mode: UsageMode,
  eventsReport: UsageEventsReport | null,
  eventsLoading: boolean,
  missesReport: UsageMissesReport | null,
  missesLoading: boolean
) {
  const fiveHour = sumRequests(report, "five_hour");
  const query = search.trim().toLowerCase();
  const activeUserRows = activeUserSummaries(report);
  const signupRows = signupSummaries(report, activeUserRows);
  const userRows = mode === "allUsers" ? signupRows : activeUserRows;
  const users = paginate(filterSummaries(userRows, query), pages.userPage, pages.pageSize);
  const misses = paginate(filterMisses(missesReport?.misses || [], query), pages.libraryPage, pages.pageSize);
  const userTitle = mode === "allUsers" ? "All signups" : "Active users";
  return `
    <div class="summary-grid usage-summary-grid">
      ${metric("5h requests", fiveHour, "current quota window")}
      ${metric("Active users", activeUserRows.length, "users with requests")}
      ${metric("Signups", productUsers(report).length, "frontend accounts")}
      ${metric("SDKs", countScopes(report, "library"), "queried packages")}
    </div>
    <div class="usage-mode-row">
      <div>
        <strong>${mode === "sdk" ? "Library Misses" : userTitle}</strong>
        <p>${mode === "sdk" ? "Resolve and docs requests that missed indexed documentation." : "Search product users and inspect their latest tool calls."}</p>
      </div>
      <div class="usage-mode-switch">
        ${usageModeButton("activeUsers", mode, "Active", activeUserRows.length)}
        ${usageModeButton("allUsers", mode, "Signups", signupRows.length)}
        ${usageModeButton("sdk", mode, "Library Misses", misses.total)}
      </div>
    </div>
    <div class="usage-admin-grid">
      ${mode === "sdk" ? missesPanel(misses, missesLoading) : leaderboardPanel(userTitle, users, "user", "No matching users.")}
      ${detailPanel(report, detail, eventsReport, eventsLoading, missesReport, missesLoading)}
    </div>
  `;
}

export function renderUsageLimits(report: UsageAdminReport) {
  const policies = [...report.policies].sort((left, right) => windowOrder.indexOf(left.windowKind) - windowOrder.indexOf(right.windowKind));
  return `
    <div class="summary-grid limits-summary-grid">
      ${policyMetric(policies, "minute", "RPM", "requests per minute")}
      ${policyMetric(policies, "hour", "RPH", "requests per hour")}
      ${policyMetric(policies, "five_hour", "5h", "requests per user window")}
      ${policyMetric(policies, "month", "Month", "monthly safety cap")}
    </div>
    <div class="limits-layout">
      <section class="panel">
        <div class="panel-head"><h2>Rate limits</h2><span class="muted">${escapeHTML(report.generatedAt || "-")}</span></div>
        <div class="usage-policy-list">${policies.map(renderPolicy).join("") || empty("No policies.")}</div>
      </section>
      <section class="panel limits-note-panel">
        <div class="panel-head"><h2>What this controls</h2></div>
        <div class="limits-note-body">
          <p><strong>RPM</strong><span>Short burst protection.</span></p>
          <p><strong>RPH</strong><span>Sustained traffic protection.</span></p>
          <p><strong>5h</strong><span>User quota window before reset.</span></p>
          <p><strong>Month</strong><span>Account-level abuse guard.</span></p>
        </div>
      </section>
    </div>
  `;
}

export function usagePolicyFromNode(node: HTMLElement): UsageLimitPolicy | null {
  const tool = node.dataset.tool || "";
  const windowKind = node.dataset.windowKind || "";
  const limitInput = node.querySelector<HTMLInputElement>("[data-usage-policy-limit]");
  const enabledInput = node.querySelector<HTMLInputElement>("[data-usage-policy-enabled]");
  const limitValue = Number(limitInput?.value || 0);
  if (!tool || !windowKind || !Number.isFinite(limitValue) || limitValue < 1) return null;
  return {
    tool,
    windowKind,
    limitValue: Math.floor(limitValue),
    enabled: Boolean(enabledInput?.checked)
  };
}

export function formatUsageWindow(kind: string) {
  if (kind === "five_hour") return "5 hours";
  return kind.replace(/_/g, " ");
}

function renderPolicy(policy: UsageLimitPolicy) {
  return `
    <article class="usage-policy-row" data-usage-policy data-tool="${escapeHTML(policy.tool)}" data-window-kind="${escapeHTML(policy.windowKind)}">
      <div>
        <strong>${escapeHTML(formatPolicyWindow(policy.windowKind))}</strong>
        <p>${escapeHTML(policy.tool)}${policy.updatedBy ? ` · by ${escapeHTML(policy.updatedBy)}` : ""}</p>
      </div>
      <label>Limit<input data-usage-policy-limit type="number" min="1" value="${policy.limitValue}" /></label>
      <label class="check-row"><input data-usage-policy-enabled type="checkbox" ${policy.enabled ? "checked" : ""} /> Enabled</label>
      <button type="button" data-save-usage-policy data-icon="check">Save</button>
    </article>
  `;
}

function policyMetric(policies: UsageLimitPolicy[], windowKind: string, label: string, hint: string) {
  const policy = policies.find((row) => row.windowKind === windowKind);
  if (!policy) return metric(label, 0, "not configured");
  const suffix = policy.enabled ? hint : `${hint} disabled`;
  return metric(label, policy.limitValue, suffix);
}

function formatPolicyWindow(kind: string) {
  if (kind === "minute") return "RPM";
  if (kind === "hour") return "RPH";
  if (kind === "five_hour") return "5-hour";
  if (kind === "month") return "Monthly";
  return formatUsageWindow(kind);
}

function leaderboardPanel(title: string, page: PageSlice<UsageSummary>, scope: "user" | "library", emptyText: string) {
  return `
    <section class="panel">
      <div class="panel-head"><h2>${escapeHTML(title)}</h2><span class="muted">${page.total} found</span></div>
      <div class="list">
        ${page.rows.length ? page.rows.map(summaryRow).join("") : empty(emptyText)}
      </div>
      ${pagination(scope, page)}
    </section>
  `;
}

function missesPanel(page: PageSlice<UsageMissSummary>, loading: boolean) {
  return `
    <section class="panel">
      <div class="panel-head"><h2>Library Misses</h2><span class="muted">${page.total} found</span></div>
      <div class="list">
        ${loading ? empty("Loading misses.") : page.rows.length ? page.rows.map(missRow).join("") : empty("No library misses.")}
      </div>
      ${pagination("library", page)}
    </section>
  `;
}

function usageModeButton(value: UsageMode, current: UsageMode, label: string, count: number) {
  return `
    <button type="button" data-usage-mode="${value}" class="${value === current ? "active" : ""}">
      <span>${escapeHTML(label)}</span>
      <strong>${formatCount(count)}</strong>
    </button>
  `;
}

function summaryRow(row: UsageSummary) {
  const usageLine = row.requests
    ? `${row.successes} ok · ${row.notFound} missed · ${row.failures} failed · ${row.limitHits} limited`
    : signupLine(row);
  return `
    <button type="button" class="list-item clickable usage-row-button" data-usage-detail="${row.scopeType}" data-usage-scope-id="${escapeHTML(row.scopeId)}">
      <div>
        <strong>${escapeHTML(row.label)}</strong>
        <p>${escapeHTML(row.hint)}</p>
        <p>${escapeHTML(usageLine)}</p>
      </div>
      <span class="usage-row-count">${formatCount(row.requests)}</span>
    </button>
  `;
}

function missRow(row: UsageMissSummary) {
  const label = row.libraryId || row.normalizedQuery || row.sampleQuery || "-";
  const agent = topAgent(row.agentClients);
  const meta = [
    formatMissType(row.missType),
    toolLabel(row.tool),
    row.distinctUsers ? `${row.distinctUsers} users` : "",
    agent ? `${agent.client} ${agent.count}` : ""
  ].filter(Boolean).join(" · ");
  return `
    <button type="button" class="list-item clickable usage-row-button" data-usage-detail="miss" data-usage-scope-id="${escapeHTML(row.key)}">
      <div>
        <strong>${escapeHTML(label)}</strong>
        <p>${escapeHTML(row.sampleQuery || row.lastError || "-")}</p>
        <p>${escapeHTML(meta)}</p>
      </div>
      <span class="usage-row-count">${formatCount(row.count)}</span>
    </button>
  `;
}

function queryRow(row: UsageQueryRollup) {
  return `
    <article class="list-item usage-query-row">
      <div>
        <strong>${escapeHTML(row.libraryId || row.scopeId || "-")}</strong>
        <p>${escapeHTML(row.query || "-")}</p>
        <p>${escapeHTML(row.lastUserEmail || row.lastUserId || "-")} · ${escapeHTML(row.lastKeyName || row.lastKeyId || "-")}</p>
      </div>
      <div class="usage-query-side">
        ${statusBadge(row.status)}
        <span>${formatCount(row.count)}</span>
      </div>
    </article>
  `;
}

function detailPanel(report: UsageAdminReport, detail: UsageDetailKey, eventsReport: UsageEventsReport | null, eventsLoading: boolean, missesReport: UsageMissesReport | null, missesLoading: boolean) {
  if (!detail) {
    return `
      <section class="panel usage-detail-panel">
        <div class="panel-head"><h2>Detail</h2><span class="muted">select a row</span></div>
        ${empty("Click a user or library miss to inspect request windows and latest tool calls.")}
      </section>
    `;
  }
  if (detail.scopeType === "miss") {
    return missDetailPanel(missesReport?.misses.find((row) => row.key === detail.scopeId) || null, missesLoading);
  }
  const windows = report.windows
    .filter((row) => row.scopeType === detail.scopeType && row.scopeId === detail.scopeId)
    .sort((left, right) => windowOrder.indexOf(left.windowKind) - windowOrder.indexOf(right.windowKind));
  const totals = totalsFor(windows.filter((row) => row.windowKind === "month").length ? windows.filter((row) => row.windowKind === "month") : windows);
  const queries = detailQueries(report, detail).slice(0, 10);
  const user = detail.scopeType === "user" ? signupUser(report, detail.scopeId) : null;
  return `
    <section class="panel usage-detail-panel">
      <div class="panel-head">
        <h2>${escapeHTML(detail.scopeType)} detail</h2>
        <span class="muted">${escapeHTML(detail.scopeId)}</span>
      </div>
      <div class="usage-detail-summary">
        ${metric("Requests", totals.requests, "selected scope")}
        ${metric("Success", totals.successes, "completed")}
        ${metric("Misses", totals.notFound, "not_found")}
        ${metric("Failures", totals.failures, "errors")}
      </div>
      ${user ? `<div class="section-title">Account</div><div class="list">${accountRow(user)}</div>` : ""}
      <div class="section-title">Windows</div>
      <div class="list">${windows.length ? windows.map(windowRow).join("") : empty("No windows for this scope.")}</div>
      <div class="section-title">Latest activity</div>
      ${eventsList(eventsReport, eventsLoading, queries)}
    </section>
  `;
}

function missDetailPanel(row: UsageMissSummary | null, loading: boolean) {
  if (loading) {
    return `
      <section class="panel usage-detail-panel">
        <div class="panel-head"><h2>Miss detail</h2><span class="muted">loading</span></div>
        ${empty("Loading miss detail.")}
      </section>
    `;
  }
  if (!row) {
    return `
      <section class="panel usage-detail-panel">
        <div class="panel-head"><h2>Miss detail</h2><span class="muted">missing</span></div>
        ${empty("Miss detail not found.")}
      </section>
    `;
  }
  return `
    <section class="panel usage-detail-panel">
      <div class="panel-head">
        <h2>Miss detail</h2>
        <span class="muted">${escapeHTML(row.key)}</span>
      </div>
      <div class="usage-detail-summary">
        ${metric("Misses", row.count, formatMissType(row.missType))}
        ${metric("Users", row.distinctUsers, "distinct accounts")}
        ${metric("Agents", Object.keys(row.agentClients || {}).length, "reported clients")}
        ${metric("Events", row.events?.length || 0, "recent raw samples")}
      </div>
      <div class="section-title">Request</div>
      <div class="list">${missRequestRows(row)}</div>
      <div class="section-title">Recent raw events</div>
      <div class="list usage-event-list">${row.events?.length ? row.events.map(eventRow).join("") : empty("No raw events.")}</div>
    </section>
  `;
}

function missRequestRows(row: UsageMissSummary) {
  const rows = [
    ["Library", row.libraryId || "-"],
    ["Normalized query", row.normalizedQuery || "-"],
    ["Sample query", row.sampleQuery || "-"],
    ["Version", row.version || "-"],
    ["Latest user", row.latestUserEmail || "-"],
    ["Latest key", row.latestKeyName || "-"],
    ["Agents", formatAgents(row.agentClients)],
    ["Last error", row.lastError || "-"],
    ["First seen", formatDateTime(row.firstSeenAt || "")],
    ["Last seen", formatDateTime(row.lastSeenAt || "")]
  ];
  return rows.map(([label, value]) => `
    <article class="list-item usage-field-row">
      <strong>${escapeHTML(label)}</strong>
      <p>${escapeHTML(value)}</p>
    </article>
  `).join("");
}

function eventsList(eventsReport: UsageEventsReport | null, loading: boolean, fallback: UsageQueryRollup[]) {
  if (loading) return empty("Loading latest activity.");
  const events = eventsReport?.events || [];
  if (events.length) {
    return `<div class="list usage-event-list">${events.map(eventRow).join("")}</div>`;
  }
  if (fallback.length) {
    return `
      <div class="list">
        ${fallback.map(queryRow).join("")}
      </div>
    `;
  }
  return empty("No recent tool calls for this scope.");
}

function eventRow(event: UsageLogEvent) {
  const primary = event.query || event.libraryId || event.responseSummary || "-";
  const meta = [
    event.userEmail || event.userId,
    event.keyName || event.keyId,
    event.libraryId,
    event.version,
    event.agentClient,
    event.sdkVersion,
    event.missType
  ].filter(Boolean).join(" · ");
  return `
    <article class="list-item usage-event-row">
      <div>
        <strong>${escapeHTML(primary)}</strong>
        <p>${escapeHTML(meta || event.error || "-")}</p>
        ${event.responseSummary ? `<p>${escapeHTML(event.responseSummary)}</p>` : ""}
      </div>
      <div class="usage-event-side">
        <span class="usage-tool">${escapeHTML(toolLabel(event.tool))}</span>
        ${statusBadge(event.status)}
        <span>${escapeHTML(formatDateTime(event.time))}</span>
      </div>
    </article>
  `;
}

function filterMisses(rows: UsageMissSummary[], query: string) {
  if (!query) return rows;
  return rows.filter((row) => `${row.libraryId || ""} ${row.normalizedQuery || ""} ${row.sampleQuery || ""} ${row.latestUserEmail || ""} ${row.latestKeyName || ""} ${row.lastError || ""} ${Object.keys(row.agentClients || {}).join(" ")}`.toLowerCase().includes(query));
}

function windowRow(row: UsageWindow) {
  return `
    <article class="list-item">
      <div>
        <strong>${escapeHTML(formatUsageWindow(row.windowKind))}</strong>
        <p>${escapeHTML(row.windowStart)} → ${escapeHTML(row.windowEnd)}</p>
        <p>${row.successes} ok · ${row.notFound} missed · ${row.failures} failed · ${row.limitHits} limited</p>
      </div>
      <span class="usage-row-count">${formatCount(row.requests)}</span>
    </article>
  `;
}

function activeUserSummaries(report: UsageAdminReport) {
  const rows = topSummaries(report, "user");
  if (!report.users) return rows;
  const allowed = new Set(productUsers(report).map((user) => user.userId));
  return rows.filter((row) => allowed.has(row.scopeId));
}

function signupSummaries(report: UsageAdminReport, activeRows: UsageSummary[]) {
  const active = new Map(activeRows.map((row) => [row.scopeId, row]));
  return productUsers(report).map((user) => {
    const row = active.get(user.userId);
    return {
      scopeType: "user" as const,
      scopeId: user.userId,
      label: user.displayName || user.email || user.userId,
      hint: user.email || user.userId,
      requests: row?.requests || 0,
      successes: row?.successes || 0,
      failures: row?.failures || 0,
      notFound: row?.notFound || 0,
      limitHits: row?.limitHits || 0,
      updatedAt: row?.updatedAt || user.updatedAt || user.createdAt || "",
      signedUpAt: user.createdAt,
      lastLoginAt: user.lastLoginAt,
      emailVerifiedAt: user.emailVerifiedAt,
      status: user.status
    };
  });
}

function productUsers(report: UsageAdminReport) {
  return [...(report.users || [])]
    .filter((user) => user.role !== "admin" && (user.userId || user.email))
    .sort((left, right) => (right.createdAt || "").localeCompare(left.createdAt || ""));
}

function signupUser(report: UsageAdminReport, userID: string) {
  return productUsers(report).find((user) => user.userId === userID) || null;
}

function signupLine(row: UsageSummary) {
  const parts = [
    row.signedUpAt ? `signed up ${formatDateTime(row.signedUpAt)}` : "no usage yet",
    row.lastLoginAt ? `last login ${formatDateTime(row.lastLoginAt)}` : "",
    row.emailVerifiedAt ? "verified" : "",
    row.status && row.status !== "active" ? row.status : ""
  ].filter(Boolean);
  return parts.join(" · ");
}

function accountRow(user: UsageAdminUser) {
  const meta = [
    user.createdAt ? `signed up ${formatDateTime(user.createdAt)}` : "",
    user.lastLoginAt ? `last login ${formatDateTime(user.lastLoginAt)}` : "never logged in",
    user.emailVerifiedAt ? "verified" : "unverified"
  ].filter(Boolean).join(" · ");
  return `
    <article class="list-item">
      <div>
        <strong>${escapeHTML(user.displayName || user.email || user.userId)}</strong>
        <p>${escapeHTML(user.email || user.userId)}</p>
        <p>${escapeHTML(meta)}</p>
      </div>
      <span class="usage-row-count">${escapeHTML(user.status || "-")}</span>
    </article>
  `;
}

function topSummaries(report: UsageAdminReport, scopeType: UsageScope) {
  const rows = report.windows.filter((row) => row.scopeType === scopeType && row.windowKind === "month");
  const source = rows.length ? rows : report.windows.filter((row) => row.scopeType === scopeType);
  const map = new Map<string, UsageSummary>();
  for (const row of source) {
    const scopeId = row.scopeId || row.userId || row.keyId || row.libraryId || "unknown";
    const existing = map.get(scopeId) || newSummary(scopeType, scopeId, row);
    existing.requests += row.requests || 0;
    existing.successes += row.successes || 0;
    existing.failures += row.failures || 0;
    existing.notFound += row.notFound || 0;
    existing.limitHits += row.limitHits || 0;
    existing.updatedAt = row.updatedAt || existing.updatedAt;
    map.set(scopeId, existing);
  }
  return Array.from(map.values()).sort((left, right) => right.requests - left.requests);
}

type PageSlice<T> = {
  rows: T[];
  page: number;
  pageCount: number;
  total: number;
};

function paginate<T>(rows: T[], page: number, pageSize: number): PageSlice<T> {
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(Math.max(1, page), pageCount);
  const start = (safePage - 1) * pageSize;
  return {
    rows: rows.slice(start, start + pageSize),
    page: safePage,
    pageCount,
    total: rows.length
  };
}

function pagination<T>(scope: "user" | "library", page: PageSlice<T>) {
  if (page.pageCount <= 1) return "";
  return `
    <div class="pagination usage-pagination">
      <button type="button" data-usage-page="${scope}" data-usage-page-target="${page.page - 1}" ${page.page <= 1 ? "disabled" : ""}>Previous</button>
      <span>Page ${page.page} of ${page.pageCount}</span>
      <button type="button" data-usage-page="${scope}" data-usage-page-target="${page.page + 1}" ${page.page >= page.pageCount ? "disabled" : ""}>Next</button>
    </div>
  `;
}

function newSummary(scopeType: UsageScope, scopeId: string, row: UsageWindow): UsageSummary {
  if (scopeType === "key") {
    return {
      scopeType,
      scopeId,
      label: row.keyName || row.keyId || scopeId,
      hint: `${row.userEmail || row.userId || "-"} · ${row.keyId || scopeId}`,
      requests: 0,
      successes: 0,
      failures: 0,
      notFound: 0,
      limitHits: 0,
      updatedAt: row.updatedAt || ""
    };
  }
  if (scopeType === "library") {
    return {
      scopeType,
      scopeId,
      label: row.libraryId || scopeId,
      hint: `package · ${row.windowKind}`,
      requests: 0,
      successes: 0,
      failures: 0,
      notFound: 0,
      limitHits: 0,
      updatedAt: row.updatedAt || ""
    };
  }
  return {
    scopeType,
    scopeId,
    label: row.displayName || row.userEmail || row.userId || scopeId,
    hint: row.userEmail || row.userId || scopeId,
    requests: 0,
    successes: 0,
    failures: 0,
    notFound: 0,
    limitHits: 0,
    updatedAt: row.updatedAt || ""
  };
}

function detailQueries(report: UsageAdminReport, detail: UsageDetailKey) {
  if (!detail) return [];
  return queryRows(report).filter((row) => {
    if (detail.scopeType === "key") return row.lastKeyId === detail.scopeId;
    return row.scopeType === detail.scopeType && row.scopeId === detail.scopeId;
  });
}

function queryRows(report: UsageAdminReport, status = "") {
  const libraryRows = report.queries.filter((row) => row.scopeType === "library");
  const rows = libraryRows.length ? libraryRows : report.queries;
  return rows
    .filter((row) => !status || row.status === status)
    .sort((left, right) => right.count - left.count);
}

function filterSummaries(rows: UsageSummary[], query: string) {
  if (!query) return rows;
  return rows.filter((row) => `${row.label} ${row.hint} ${row.scopeId}`.toLowerCase().includes(query));
}

function countScopes(report: UsageAdminReport, scopeType: UsageScope) {
  const rows = report.windows.filter((row) => row.scopeType === scopeType && row.windowKind === "month");
  const source = rows.length ? rows : report.windows.filter((row) => row.scopeType === scopeType);
  return new Set(source.map((row) => row.scopeId || row.userId || row.keyId || row.libraryId).filter(Boolean)).size;
}

function sumRequests(report: UsageAdminReport, windowKind: string) {
  return report.windows
    .filter((row) => row.scopeType === "user" && row.windowKind === windowKind)
    .reduce((sum, row) => sum + (row.requests || 0), 0);
}

function totalsFor(rows: UsageWindow[]) {
  return rows.reduce(
    (sum, row) => ({
      requests: sum.requests + (row.requests || 0),
      successes: sum.successes + (row.successes || 0),
      failures: sum.failures + (row.failures || 0),
      notFound: sum.notFound + (row.notFound || 0),
      limitHits: sum.limitHits + (row.limitHits || 0)
    }),
    { requests: 0, successes: 0, failures: 0, notFound: 0, limitHits: 0 }
  );
}

function metric(label: string, value: number, hint: string) {
  return `<div class="metric"><span>${escapeHTML(label)}</span><strong>${formatCount(value)}</strong><em>${escapeHTML(hint)}</em></div>`;
}

function statusBadge(status: string) {
  return `<span class="status ${escapeHTML(status)}">${escapeHTML(status || "unknown")}</span>`;
}

function toolLabel(tool: string) {
  if (tool === "get-library-docs") return "Docs";
  if (tool === "resolve-library-id") return "Resolve";
  return tool || "Tool";
}

function formatMissType(value: string) {
  return (value || "miss").replace(/_/g, " ");
}

function topAgent(clients?: Record<string, number>) {
  const entries = Object.entries(clients || {});
  if (!entries.length) return null;
  entries.sort((left, right) => right[1] - left[1]);
  return { client: entries[0][0], count: entries[0][1] };
}

function formatAgents(clients?: Record<string, number>) {
  const entries = Object.entries(clients || {}).sort((left, right) => right[1] - left[1]);
  if (!entries.length) return "-";
  return entries.map(([client, count]) => `${client} ${count}`).join(", ");
}

function formatDateTime(value: string) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function empty(text: string) {
  return `<p class="empty">${escapeHTML(text)}</p>`;
}

function formatCount(value: number) {
  return new Intl.NumberFormat().format(value || 0);
}

function escapeHTML(value: unknown) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
